import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Chip } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';
import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import Photos from '../../components/Slider/icons/Photos';
import Video from '../../components/Slider/icons/Video';
import Map from '../../components/Slider/icons/Map';

import './ImageMosaic.scss';

const extractMainImages = (images, count) => images.slice(0, count);

const ImageMosaic = ({
  imageTrack = [],
  onClick = () => {},
  onVideoClick = undefined,
  onMapClick = undefined
}) => {
  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeaderV2').get('behaviour') ===
    'variant';

  const showFiveImages =
    imageTrack.length >= 5 && isListingSimplificationHeaderVariant;
  const imageCount = showFiveImages ? 5 : 3;
  const images = extractMainImages(imageTrack, imageCount);
  const [
    leftImage,
    rightImageTop,
    rightImageBottom,
    extraRightTop,
    extraRightBottom
  ] = images;

  const hasVideoChip = typeof onVideoClick === 'function';
  const hasMapChip = typeof onMapClick === 'function';

  const showVideoChip = isListingSimplificationHeaderVariant && hasVideoChip;
  const showMapChip = isListingSimplificationHeaderVariant && hasMapChip;

  const mosaicClassName = classNames('imageMosaic', {
    'imageMosaic--five-images': showFiveImages,
    'imageMosaic--three-images-expanded':
      isListingSimplificationHeaderVariant && imageCount === 3
  });

  const chipsSize = isListingSimplificationHeaderVariant ? 'big' : 'medium';
  const allPhotosChipsTransKey = isListingSimplificationHeaderVariant
    ? 'slider.carousel.all.photos'
    : 'mosaic.chip.text';
  const renderPhotoIcon = () =>
    isListingSimplificationHeaderVariant ? (
      <DSIcon name="photo_camera" />
    ) : (
      <Photos theme="interactionDark" />
    );

  const renderMapIcon = () =>
    isListingSimplificationHeaderVariant ? (
      <DSIcon name="location_on_filled" />
    ) : (
      <Map theme="interactionDark" />
    );

  const renderVideoIcon = () =>
    isListingSimplificationHeaderVariant ? (
      <DSIcon name="smart_display" />
    ) : (
      <Video theme="interactionDark" />
    );

  const bottomOverlayClass = 'imageMosaic__bottom-overlay';
  const leftImageClassName = classNames('imageMosaic__left_image', {
    [bottomOverlayClass]:
      isListingSimplificationHeaderVariant && (showMapChip || showVideoChip)
  });

  const rightImageBottomClassName = classNames(
    'imageMosaic__right_image_bottom',
    {
      [bottomOverlayClass]:
        isListingSimplificationHeaderVariant && !showFiveImages
    }
  );
  const extraRightBottomClassName = classNames(
    'imageMosaic__extra_right_bottom',
    {
      [bottomOverlayClass]:
        isListingSimplificationHeaderVariant && showFiveImages
    }
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div className={mosaicClassName} onClick={onClick} role="img">
      <div className={leftImageClassName}>
        <img src={leftImage.src} alt="left_image" rel="preload" />
      </div>
      <div className="imageMosaic__right_image_top">
        <img src={rightImageTop.src} alt="right_image_top" rel="preload" />
      </div>
      <div className={rightImageBottomClassName}>
        <img
          src={rightImageBottom.src}
          alt="right_image_bottom"
          rel="preload"
        />
      </div>
      {showFiveImages && extraRightTop && (
        <div className="imageMosaic__extra_right_top">
          <img src={extraRightTop.src} alt="extra_right_top" rel="preload" />
        </div>
      )}
      {showFiveImages && extraRightBottom && (
        <div className={extraRightBottomClassName}>
          <img
            src={extraRightBottom.src}
            alt="extra_right_bottom"
            rel="preload"
          />
        </div>
      )}
      <div className="imageMosaic__chips-wrapper">
        <div className="imageMosaic__chips imageMosaic__chips--left">
          {showMapChip && (
            <Chip.Choice
              renderLeadingIcon={renderMapIcon}
              className="imageMosaic__chip imageMosaic__chip--map"
              onClick={e => {
                e.stopPropagation();
                onMapClick();
              }}
              label={trans('slider.carousel.view.on.map')}
              size={chipsSize}
            />
          )}
          {showVideoChip && (
            <Chip.Choice
              renderLeadingIcon={renderVideoIcon}
              className="imageMosaic__chip imageMosaic__chip--video"
              onClick={e => {
                e.stopPropagation();
                onVideoClick();
              }}
              label={trans('slider.carousel.video')}
              size={chipsSize}
            />
          )}
        </div>
        <div className="imageMosaic__chips imageMosaic__chips--right">
          <Chip.Choice
            renderLeadingIcon={renderPhotoIcon}
            className="imageMosaic__chip imageMosaic__right_image_bottom__chip"
            onClick={onClick}
            label={trans(allPhotosChipsTransKey)}
            size={chipsSize}
          />
        </div>
      </div>
    </div>
  );
};

ImageMosaic.propTypes = {
  imageTrack: PropTypes.arrayOf(
    PropTypes.shape({
      caption: PropTypes.string,
      src: PropTypes.string.isRequired
    })
  ),
  onClick: PropTypes.func,
  onVideoClick: PropTypes.func,
  onMapClick: PropTypes.func
};

export default ImageMosaic;
